import { useEffect, useState, type FormEvent } from "react";
import { api, FalhaApi, type UsuarioResumoApi } from "../api";
import { useSessao } from "../estado/sessao";
import { Layout } from "../componentes/Layout";
import { iniciais } from "../componentes/Pill";

const RETULO_PAPEL: Record<string, string> = { ADMIN: "Administrador", VISTORIADOR: "Vistoriador" };

export function Equipe() {
  const { usuario: eu } = useSessao();
  const [usuarios, setUsuarios] = useState<UsuarioResumoApi[] | null>(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  function carregar() {
    api
      .listarUsuarios()
      .then(({ usuarios }) => setUsuarios(usuarios))
      .catch(() => setErro("Falha ao carregar equipe"));
  }

  useEffect(carregar, []);

  async function alternarAtivo(u: UsuarioResumoApi) {
    try {
      await api.editarUsuario(u.id, { ativo: !u.ativo });
      carregar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao atualizar");
    }
  }

  async function trocarPapel(u: UsuarioResumoApi, papel: "ADMIN" | "VISTORIADOR") {
    try {
      await api.editarUsuario(u.id, { papel });
      carregar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao atualizar");
    }
  }

  return (
    <Layout>
      <main className="container">
        <h2>Equipe</h2>

        {erro && (
          <div className="alerta alerta-erro" role="alert">
            {erro}
          </div>
        )}

        {mostrarForm ? (
          <FormularioUsuario
            aoCancelar={() => setMostrarForm(false)}
            aoCriar={() => { setMostrarForm(false); carregar(); }}
          />
        ) : (
          <button className="botao botao-primario" onClick={() => setMostrarForm(true)}>
            + Novo usuário
          </button>
        )}

        {usuarios === null ? (
          <p style={{ marginTop: 16 }}>Carregando…</p>
        ) : (
          <div className="cartao" style={{ padding: 0, marginTop: 16 }}>
            {usuarios.map((u) =>
              editandoId === u.id ? (
                <FormularioEdicao
                  key={u.id}
                  usuario={u}
                  aoCancelar={() => setEditandoId(null)}
                  aoSalvar={() => {
                    setEditandoId(null);
                    carregar();
                  }}
                />
              ) : (
                <div
                  key={u.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    padding: 16,
                    borderBottom: "1px solid var(--borda)",
                    opacity: u.ativo ? 1 : 0.5,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                    <span className="avatar-inicial" style={{ width: 36, height: 36, fontSize: 14 }}>
                      {iniciais(u.nome)}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <strong>{u.nome}</strong>
                      {u.id === eu?.id && <span style={{ color: "var(--texto-suave)" }}> (você)</span>}
                      <div style={{ fontSize: 13, color: "var(--texto-suave)" }}>
                        {u.email} · {!u.ativo && "Desativado · "}
                        {u.ultimoLoginEm
                          ? `último acesso ${new Date(u.ultimoLoginEm).toLocaleDateString("pt-BR")}`
                          : "nunca acessou"}
                      </div>
                    </div>
                  </div>

                  {u.id === eu?.id ? (
                    <span className={`pill ${u.papel === "ADMIN" ? "pill-azul" : "pill-neutro"}`}>
                      {RETULO_PAPEL[u.papel]}
                    </span>
                  ) : (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button className="botao botao-texto" onClick={() => setEditandoId(u.id)}>
                        Editar
                      </button>
                      <select
                        value={u.papel}
                        onChange={(e) => trocarPapel(u, e.target.value as "ADMIN" | "VISTORIADOR")}
                        style={{ width: "auto" }}
                      >
                        <option value="ADMIN">Administrador</option>
                        <option value="VISTORIADOR">Vistoriador</option>
                      </select>
                      <button className="botao botao-texto" onClick={() => void alternarAtivo(u)}>
                        {u.ativo ? "Desativar" : "Reativar"}
                      </button>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </main>
    </Layout>
  );
}

function FormularioEdicao({
  usuario,
  aoCancelar,
  aoSalvar,
}: {
  usuario: UsuarioResumoApi;
  aoCancelar: () => void;
  aoSalvar: () => void;
}) {
  const [nome, setNome] = useState(usuario.nome);
  const [email, setEmail] = useState(usuario.email);
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      if (senha && senha !== confirmacao) throw new Error("A confirmação da senha não confere");
      await api.editarUsuario(usuario.id, { nome, email });
      if (senha) await api.redefinirSenhaUsuario(usuario.id, senha);
      aoSalvar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao salvar");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={aoEnviar} style={{ padding: 16, borderBottom: "1px solid var(--borda)" }}>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}

      <label htmlFor={`ed-nome-${usuario.id}`}>Nome</label>
      <input id={`ed-nome-${usuario.id}`} value={nome} onChange={(e) => setNome(e.target.value)} required autoFocus />

      <label htmlFor={`ed-email-${usuario.id}`}>E-mail</label>
      <input
        id={`ed-email-${usuario.id}`}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />

      <label htmlFor={`ed-senha-${usuario.id}`}>Nova senha (opcional)</label>
      <input id={`ed-senha-${usuario.id}`} type="password" minLength={10} value={senha} onChange={(e) => setSenha(e.target.value)} />
      {senha && <><label htmlFor={`ed-confirmar-${usuario.id}`}>Confirmar nova senha</label><input id={`ed-confirmar-${usuario.id}`} type="password" minLength={10} value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} required /></>}

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button className="botao botao-primario" style={{ marginTop: 0 }} type="submit" disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <button className="botao botao-texto" type="button" onClick={aoCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

function FormularioUsuario({
  aoCancelar,
  aoCriar,
}: {
  aoCancelar: () => void;
  aoCriar: () => void;
}) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [papel, setPapel] = useState<"ADMIN" | "VISTORIADOR">("VISTORIADOR");
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      if (senha !== confirmacao) throw new Error("A confirmação da senha não confere");
      await api.criarUsuario({ nome, email, papel, senha });
      aoCriar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao criar usuário");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form className="cartao" style={{ padding: 16, marginTop: 12 }} onSubmit={aoEnviar}>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}

      <label htmlFor="eq-nome">Nome</label>
      <input id="eq-nome" value={nome} onChange={(e) => setNome(e.target.value)} required />

      <label htmlFor="eq-email">E-mail</label>
      <input id="eq-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />

      <label htmlFor="eq-papel">Papel</label>
      <select id="eq-papel" value={papel} onChange={(e) => setPapel(e.target.value as "ADMIN" | "VISTORIADOR")}>
        <option value="VISTORIADOR">Vistoriador</option>
        <option value="ADMIN">Administrador</option>
      </select>

      <label htmlFor="eq-senha">Senha inicial</label>
      <input id="eq-senha" type="password" minLength={10} value={senha} onChange={(e) => setSenha(e.target.value)} required />
      <label htmlFor="eq-confirmar-senha">Confirmar senha</label>
      <input id="eq-confirmar-senha" type="password" minLength={10} value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} required />

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button className="botao botao-primario" style={{ marginTop: 0 }} type="submit" disabled={salvando}>
          {salvando ? "Criando…" : "Criar usuário"}
        </button>
        <button className="botao botao-texto" type="button" onClick={aoCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
